import isEqual from "fast-deep-equal/es6";
import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { MoreToggle } from "../../components/MoreToggle";
import { cubeGeometry } from "../../state/cubeGeometry";
import {
  useOmgeving,
  type Analysis,
  type Omgeving as OmgevingData,
  type OmgevingDocument,
  type Regulation,
} from "../../state/omgeving";
import { Cube } from "../../state/types";
import {
  analysisText,
  articleLabel,
  mapLink,
  regulationLine,
  sortRegulations,
} from "./omgevingFormat";
import { A, Info, KV, SubHeader } from "./util";

const SHOW = 5;

type PublicLinks = { url?: string | null; kaartUrl?: string | null };

/** Titel naar de tekst, en Regels op de kaart ernaast als die ergens anders heen gaat. */
const Title = ({ item, children }: { item: PublicLinks; children: React.ReactNode }) => {
  const map = mapLink(item);
  return (
    <>
      {item.url ? <A href={item.url}>{children}</A> : children}
      {map && (
        <>
          {" "}
          <A href={map}>kaart</A>
        </>
      )}
    </>
  );
};

const Muted = ({ children }: { children: React.ReactNode }) => (
  <div className="text-sm text-gray-600">{children}</div>
);

const List = <T,>({
  heading,
  rows,
  Row,
}: {
  heading: string;
  rows: T[];
  Row: React.FC<{ r: T }>;
}) => {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  return (
    <KV k={`${heading} (${rows.length})`}>
      {(open ? rows : rows.slice(0, SHOW)).map((r, i) => (
        <Row key={i} r={r} />
      ))}
      {rows.length > SHOW && <MoreToggle open={open} setOpen={setOpen} />}
    </KV>
  );
};

const RegulationRow = ({ r }: { r: Regulation }) => (
  <div className="py-1">
    <Title item={r}>{r.titel}</Title>
    <Muted>{regulationLine(r)}</Muted>
  </div>
);

const DocumentRow = ({ r }: { r: OmgevingDocument }) => (
  <div className="py-1">
    <Title item={r}>{r.titel}</Title>
    {r.soort && <Muted>{r.soort}</Muted>}
  </div>
);

const AnalysisLine = ({ a }: { a: Analysis }) => {
  const text = analysisText(a.analyse, a.resultaat);
  if (!text) return null;
  const article = articleLabel(a.artikel);
  const source = [a.regeling, article].filter(Boolean).join(", ");
  return (
    <div className="py-1">
      {a.analyse}: {text}
      {source && <Muted>{a.url ? <A href={a.url}>{source}</A> : source}</Muted>}
    </div>
  );
};

const Content = ({ omgeving }: { omgeving: OmgevingData }) => {
  const regulations = sortRegulations(omgeving.regelingen ?? []);
  const documents = omgeving.documenten ?? [];
  const analyses = omgeving.analyses ?? [];
  const plans = omgeving.imro?.plannen ?? [];
  return (
    <>
      {(regulations.length > 0 || documents.length > 0) && (
        <>
          <SubHeader>Regelingen en beleid (DSO)</SubHeader>
          <Info as="dl">
            <List heading="Geldende regelingen" rows={regulations} Row={RegulationRow} />
            <List heading="Plannen en visies" rows={documents} Row={DocumentRow} />
          </Info>
        </>
      )}
      {(analyses.length > 0 || plans.length > 0) && (
        <>
          <SubHeader>Regels op deze locatie</SubHeader>
          <Info as="dl">
            {analyses.length > 0 && (
              <KV k="Uit de regelteksten">
                {analyses.map((a, i) => (
                  <AnalysisLine key={i} a={a} />
                ))}
              </KV>
            )}
            {/* Bestemming en bouwvlak staan al hierboven, uit de cube */}
            {plans.map((p, i) => (
              <KV key={i} k={p.naam}>
                <Title item={p}>{[p.soort, p.status].filter(Boolean).join(", ")}</Title>
                {p.aanduidingen.length > 0 && <div>Aanduidingen: {p.aanduidingen.join("; ")}</div>}
                {p.gebiedsaanduidingen.length > 0 && (
                  <div>Gebiedsaanduidingen: {p.gebiedsaanduidingen.join("; ")}</div>
                )}
                {p.maatvoeringen.length > 0 && <div>Maatvoering: {p.maatvoeringen.join("; ")}</div>}
                {p.analyses.map((a, j) => (
                  <AnalysisLine key={j} a={a} />
                ))}
              </KV>
            ))}
          </Info>
        </>
      )}
    </>
  );
};

const isEmpty = (e?: OmgevingData) =>
  !e?.regelingen?.length && !e?.documenten?.length && !e?.imro?.plannen?.length;

/**
 * Wat het Omgevingsloket over deze plek weet, via de omgevingsmonitor:
 * regelingen, plannen en visies, en de regels die daaruit op de locatie
 * vallen. Bestemming en bouwvlak niet — die staan al in deze sectie uit de
 * cube, en dezelfde bestemming tweemaal laat de lezer raden welke telt.
 *
 * Dichtgeklapt tot de lezer erom vraagt: een vers gebied kost de monitor tot
 * 90 seconden. De regel staat er dus ook als er niets blijkt te zijn — dat is
 * de prijs van lui laden.
 */
export const Omgeving = ({ cube }: { cube: Cube }) => {
  const geometry = useMemo(() => cube?.geometry && cubeGeometry(cube), [cube]);
  // Open voor díé geometrie: klik je naar een ander perceel, dan klapt hij
  // dicht in plaats van meteen de volgende trage vraag af te vuren
  const [openFor, setOpenFor] = useState<GeoJSON.Geometry>();
  const open = !!geometry && isEqual(openFor, geometry);
  const [fetchedFor, state, omgeving, load] = useOmgeving(
    useShallow((s) => [s.geometry, s.state, s.omgeving, s.load])
  );

  useEffect(() => {
    if (open) load(geometry);
  }, [open, geometry, load]);

  if (!geometry) return null;
  // De store kan nog het antwoord voor het vorige perceel dragen
  const current = isEqual(fetchedFor, geometry);
  const done = current && (state === "fetched" || state === "error");
  return (
    <>
      <SubHeader>Omgevingsloket</SubHeader>
      <Info as="dl">
        <KV k="Regelingen, plannen en regels">
          <MoreToggle open={open} setOpen={(o: boolean) => setOpenFor(o ? geometry : undefined)} />
          {open && state === "fetching" && (
            <Muted>Ophalen bij het Omgevingsloket; dit kan een minuut duren…</Muted>
          )}
          {/* Een storing is stilte, net als bij de bekendmakingen */}
          {open && done && isEmpty(omgeving) && <Muted>Geen omgevingsinformatie gevonden.</Muted>}
        </KV>
      </Info>
      {open && done && omgeving && <Content omgeving={omgeving} />}
    </>
  );
};
